import { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Box,
  Typography,
} from "@mui/material";

import "../styles.css";
import BubbleCard, { ExpandedBubble } from "./Bubble";
import { useDocs } from "../../../hooks/useDocs";

export default function BubbleContainer({ id }) {
  const [gmContent, setGmContent] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [selectedBubble, setSelectedBubble] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [autoIndex, setAutoIndex] = useState(0);

  const { getAllExperiencesWithBubbles } = useDocs();

  // Handle click on a bubble
  const handleClick = useCallback(
    (bubble) => {
      setLastActivity(Date.now());

      if (selectedBubble?.id === bubble.id) {
        setSelectedBubble(bubble);
        return;
      } else if (!expanded) {
        setSelectedBubble(bubble);
        setExpanded(true);
      }
    },
    [selectedBubble, expanded]
  );

  useEffect(() => {
    handleClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[id])

  const handleClose = useCallback(() => {
    setExpanded(false);
    setSelectedBubble(null);
  }, []);

  // // Outside click handling with class-based lookup
  const handleOutsideClick = useCallback(
    (event) => {
      setLastActivity(Date.now());

      const expandedEl = document.querySelector(".expanded-content");
      if (!expandedEl) return;
      if (expandedEl.contains(event.target)) return;

      const clickedBubbleEl = event.target.closest(".bubble");
      if (clickedBubbleEl) {
        const bubbleId = clickedBubbleEl.getAttribute("data-id");
        const foundBubble = gmContent
          .flatMap((section) => section.bubbles)
          .find((b) => String(b.id) === bubbleId);

        if (foundBubble) {
          if (selectedBubble?.id === foundBubble.id) {
            setSelectedBubble(null);
          } else {
            setSelectedBubble(foundBubble);
            setExpanded(true);
          }
          return;
        }
      }
    },
    [gmContent, selectedBubble]
  );

  // Attach/detach global listener dynamically
  useEffect(() => {
    if (expanded) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [expanded, handleOutsideClick]);

  // Fetch bubble content data
  useEffect(() => {
    getAllBubbles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NEW: detect inactivity -> start autoplay after 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (!isAutoPlaying && now - lastActivity > 2 * 60 * 1000) {
        setIsAutoPlaying(true);
        setAutoIndex(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity, isAutoPlaying]);

  // NEW: during autoplay, switch bubble every 15s
  useEffect(() => {
    if (!isAutoPlaying) return;

    const section = gmContent.find((item) => item.id === id);
    if (!section || section.bubbles.length === 0) return;

    const interval = setInterval(() => {
      const bubble = section.bubbles[autoIndex % section.bubbles.length];
      setSelectedBubble(bubble);
      setExpanded(true);
      setAutoIndex((prev) => prev + 1);
    }, 15000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, gmContent, id, autoIndex]);

  // NEW: stop autoplay when user interacts
  useEffect(() => {
    const stopAutoPlay = () => {
      setLastActivity(Date.now());
      setIsAutoPlaying(false);
    };

    window.addEventListener("click", stopAutoPlay);
    window.addEventListener("keydown", stopAutoPlay);
    return () => {
      window.removeEventListener("click", stopAutoPlay);
      window.removeEventListener("keydown", stopAutoPlay);
    };
  }, []);

  const getAllBubbles = async () => {
    const data = await getAllExperiencesWithBubbles();
    setGmContent(data);
  };

  return (
    <Box className="relative flex justify-center items-center w-full !m-0 bubbles_height">
      <Box className="bubbles-container">
        {/* Render all bubbles */}
        {gmContent
          .filter((item) => item.id === id)
          .map((section, index) => (
            <Grid
              item
              xs={12}
              key={index}
              className={`bubble_sec ${
                id === "lifeScience" ? "active" : "inactive"
              }`}
            >
              {section.bubbles.map((bubble, i) => {
                let bgColor = bubble.lightColor;
                let color = "#fff"
                if (selectedBubble) {
                  if (selectedBubble?.id === bubble?.id) {
                    bgColor = bubble.activeColor;
                  } else {
                    bgColor = bubble.deactiveColor;
                    color = "#ffffff80"
                  }
                }

                return (
                  <Grid item key={i}>
                    <BubbleCard
                      {...bubble}
                      className="bubble"
                      data-id={bubble.id}
                      bgColor={bgColor}
                      color={color}
                      handleClick={() => handleClick(bubble)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ))}

        {/* Expanded bubble content */}
        <Box
          className={`expanded-content ${
            selectedBubble && selectedBubble?.content
              ? "active_expanded"
              : "normal_expanded"
          }`}
          sx={{
            p: 4,
            overflowY: "auto",
            transition: "all 0.3s ease",
          }}
        >
          {selectedBubble?.content && (
            <ExpandedBubble
              {...selectedBubble.content}
              darkColor={selectedBubble.darkColor ?? "#ffffff"}
              handleClose={handleClose}
            />
          )}

          {!(selectedBubble && selectedBubble?.content) &&
            gmContent.filter((item) => item.id === id)[0]?.heading && (
              <Box
                className={`bubble_tiles items-center ${
                  selectedBubble && selectedBubble?.content
                    ? "active_expanded"
                    : "normal_expanded"
                }`}
              >
                <Typography
                  variant="h6"
                  className="exerience-heading"
                  component="h4"
                >
                  {gmContent.find((item) => item.id === id)?.heading}
                </Typography>

                <Typography
                  variant="body1"
                  className="experience-description"
                  component="p"
                >
                  {gmContent.find((item) => item.id === id)?.description}
                </Typography>
              </Box>
            )}
        </Box>
      </Box>
    </Box>
  );
}
